/**
 * useWebRTCChat Hook
 * Manages WebRTC connection to the AI service
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    connectSignalingWebSocket,
    fetchOffer,
    getIceServers,
    IceCandidateBuffer,
    sendAnswer,
    sendIceCandidate,
    WebRTCServiceError
} from '../services/webrtcSignaling';
import {
    WebRTCMessage,
    WebRTCServerReply
} from '../types/webrtc';

interface UseWebRTCChatProps {
  restaurantId: string;
  tableId: string;
  onMessage?: (data: WebRTCServerReply) => void;
  onError?: (error: string) => void;
}

interface UseWebRTCChatReturn {
  isConnected: boolean;
  connectionState: RTCPeerConnectionState;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: WebRTCMessage) => void;
}

export function useWebRTCChat({
  restaurantId,
  tableId,
  onMessage,
  onError,
}: UseWebRTCChatProps): UseWebRTCChatReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');
  const [error, setError] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const iceBufferRef = useRef<IceCandidateBuffer | null>(null);
  const isConnectingRef = useRef(false);

  const disconnect = useCallback(() => {
    console.log('[WebRTCChat] Disconnecting...');
    
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    setIsConnected(false);
    setConnectionState('closed');
    iceBufferRef.current = null;
  }, []);

  const connect = useCallback(async () => {
    if (isConnectingRef.current || isConnected) return;
    isConnectingRef.current = true;
    
    try {
      setError(null);
      console.log('[WebRTCChat] Starting connection...', {
        restaurantId,
        tableId,
        windowLocation: window.location.href,
      });

      // 1. Get ICE Servers
      console.log('[WebRTCChat] Step 1: Fetching ICE servers...');
      const iceData = await getIceServers();
      const sessionId = iceData.session_id;
      console.log('[WebRTCChat] Step 1: ICE servers retrieved successfully', {
        sessionId,
        iceServersCount: iceData.iceServers?.length || 0,
      });

      // 2. Open WebSocket FIRST (critical for ICE exchange)
      wsRef.current = connectSignalingWebSocket(sessionId);
      
      await new Promise<void>((resolve, reject) => {
        if (!wsRef.current) return reject(new Error('WebSocket not initialized'));
        
        const timeout = setTimeout(() => reject(new Error('WebSocket connection timeout')), 10000);
        
        wsRef.current.onopen = () => {
          clearTimeout(timeout);
          resolve();
        };
        
        wsRef.current.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('WebSocket connection failed'));
        };
      });

      // 3. Setup Media
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // 4. Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: iceData.iceServers,
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });
      pcRef.current = pc;
      iceBufferRef.current = new IceCandidateBuffer(pc);

      // Add tracks
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current!);
      });

      pc.ontrack = (event) => {
        const remoteStream = event.streams[0] || new MediaStream([event.track]);
        const audio = new Audio();
        audio.srcObject = remoteStream;
        audio.play().catch(e => console.error('[WebRTCChat] Error playing remote track:', e));
      };

      pc.onconnectionstatechange = () => {
        setConnectionState(pc.connectionState);
        if (pc.connectionState === 'connected') {
          setIsConnected(true);
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          setIsConnected(false);
        }
      };

      pc.onicegatheringstatechange = () => {
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          if (wsRef.current) {
            sendIceCandidate(wsRef.current, event.candidate);
          }
        }
      };

      pc.onicecandidateerror = () => {
      };

      // WS message handler for remote ICE
      wsRef.current.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ICE' && data.candidate && iceBufferRef.current) {
            iceBufferRef.current.addCandidate(data.candidate);
          }
        } catch (e) {
          // Ignore JSON parsing errors for non-ICE messages
        }
      };

      // DataChannel setup
      pc.ondatachannel = (event) => {
        dcRef.current = event.channel;
        dcRef.current.onopen = () => {
          console.log('[WebRTCChat] DataChannel OPEN');
          
          // Automatically send an empty text message to trigger initialization/greeting
          if (dcRef.current && dcRef.current.readyState === 'open') {
            console.log('[WebRTCChat] Sending initial TXT message...');
            dcRef.current.send(JSON.stringify({ type: 'TXT', text: '' }));
          }
        };
        dcRef.current.onmessage = (msgEvent) => {
          try {
            const data = JSON.parse(msgEvent.data) as WebRTCServerReply;
            if (onMessage) onMessage(data);
          } catch (e) {
            console.error('[WebRTCChat] Error parsing DataChannel message:', e);
          }
        };
      };

      // 5. Exchange SDP
      const offer = await fetchOffer(sessionId, restaurantId, tableId);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Flush buffered ICE candidates
      if (iceBufferRef.current) {
        iceBufferRef.current.processBufferedCandidates();
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await sendAnswer(sessionId, answer);

      console.log('[WebRTCChat] Signaling complete');

    } catch (err: any) {
      console.error('[WebRTCChat] Connection failed:', err);
      const msg = err instanceof WebRTCServiceError ? err.message : 'Connection failed';
      setError(msg);
      if (onError) onError(msg);
      disconnect();
    } finally {
      isConnectingRef.current = false;
    }
  }, [restaurantId, tableId, onMessage, onError, disconnect, isConnected]);

  const sendMessage = useCallback((message: WebRTCMessage) => {
    if (dcRef.current && dcRef.current.readyState === 'open') {
      dcRef.current.send(JSON.stringify(message));
    } else {
      console.warn('[WebRTCChat] Cannot send message: DataChannel is not open');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    isConnected,
    connectionState,
    error,
    connect,
    disconnect,
    sendMessage,
  };
}
